/**
 * =========================================================
 * Material Dashboard 2 React - v2.1.0
 * =========================================================
 *
 * Product Page: https://www.creative-tim.com/product/material-dashboard-react
 * Copyright 2022 Creative Tim (https://www.creative-tim.com)
 *
 * Coded by www.creative-tim.com
 *
 * =========================================================
 *
 * The above copyright notice and this permission notice shall be included
 * in all copies or substantial portions of the Software.
 */

/**
 * This file is used for controlling the global states of the components,
 * you can customize the states for the different components here.
 */

import {
    createContext,
    useContext,
    useState,
    useMemo,
    useEffect,
    ReactNode,
} from "react";

// localforage is used for offline storage
import localforage from "localforage";
import PropTypes from "prop-types";
import { useAppServices } from "../hook/services";
import Loader from "../components/Loader";

// Material Dashboard 2 React main context
interface AgencyContextType {
    agency: Record<string, any>;
    Update: (updates: Record<string, any>) => void;
    clear: () => void;
    appId: string | null;
}

const AgencyContext = createContext<AgencyContextType | undefined>(undefined);

// Setting custom name for the context which is visible on react dev tools
AgencyContext.displayName = "AgencyContext";

// Material Dashboard 2 React context provider
interface AgencyProviderProps {
    children: ReactNode;
}

function AgencyProvider({ children }: AgencyProviderProps) {
    const search = window.location.search;
    const app_id = new URLSearchParams(search).get('app_id');
    const company_id = new URLSearchParams(search).get('company_id');
    const Service = useAppServices();
    const [agency, setAgency] = useState<Record<string, any>>({});
    const [loader, setLoader] = useState(true);
    const [appId, setAppId] = useState(app_id);

    const Update = (updates: Record<string, any>) => {
        const data = { ...agency, ...updates };
        setAgency(data);
        localforage.setItem("agency", { ...data });
    };

    const clear = () => setAgency({});

    const value = useMemo(() => ({ agency, Update, clear, appId }), [agency, Update, clear, appId]);

    const getAgency = async () => {
        const { response } = await Service.agency.get({
            query: `company_id=${company_id}`,
        });
        if (!response) return setLoader(false);

        setAgency({ ...response.data });
        localforage.setItem("agency", { ...response.data });
        return setLoader(false);
    };

    const updateAgency = async () => {
        const localAgency = await localforage.getItem("agency");
        if (!localAgency) return setLoader(false);
        setAgency({ ...localAgency });
        setLoader(false);
        return getAgency();
    };

    const onLoad = () => {
        updateAgency();
        setAppId(app_id)
    };

    useEffect(onLoad, []);

    return loader ? <Loader />
        : <AgencyContext.Provider value={value}>{children}</AgencyContext.Provider>;
}

// Material Dashboard 2 React custom hook for using context
function useAgencyInfo() {
    return useContext(AgencyContext) as AgencyContextType;
}

// Typechecking props for the MaterialUIControllerProvider
AgencyProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export { AgencyProvider, useAgencyInfo };
